import React, { useState } from 'react';
import { useAppSelector } from '@/redux/hooks/useAppSelector';
import { MapModel } from '@/types/models';
import { Autocomplete } from '@/shared/Autocomplete';
import { modelsDataSelector } from '@/redux/models/models.selectors';
import {
  getModelElementsForModel,
  updateElementSubmap,
} from '@/redux/modelElements/modelElements.thunks';
import { showToast } from '@/utils/showToast';
import { SerializedError } from '@reduxjs/toolkit';
import { elementGlyphStateSelector } from '@/redux/modal/modal.selector';
import { useAppDispatch } from '@/redux/hooks/useAppDispatch';
import { LoadingIndicator } from '@/shared/LoadingIndicator';

const LOADING_INDICATOR_SIZE = 44;

type ElementSubmapFormProps = {
  selectedMapModel: number | null;
  setSelectedMapModel: (modelId: number | null) => void;
};

export const ElementSubmapForm: React.FC<ElementSubmapFormProps> = ({
  selectedMapModel,
  setSelectedMapModel,
}) => {
  const models: MapModel[] = useAppSelector(modelsDataSelector);
  const initialSelectedMapModel = models.find(model => model.id === selectedMapModel);

  const elementGlyphState = useAppSelector(elementGlyphStateSelector);

  const [isSending, setIsSending] = useState<boolean>(false);

  const dispatch = useAppDispatch();

  const handleMapModelChange = async (mapModel: MapModel | null): Promise<void> => {
    if (!elementGlyphState) {
      return;
    }
    const submapId = mapModel?.id || null;
    setSelectedMapModel(submapId);
    setIsSending(true);
    try {
      await dispatch(
        updateElementSubmap({
          modelId: elementGlyphState.model,
          elementId: elementGlyphState.id,
          submapId,
        }),
      ).unwrap();
      showToast({
        type: 'success',
        message: 'The element submap has been successfully updated.',
      });
      dispatch(getModelElementsForModel(elementGlyphState.model));
    } catch (error) {
      const typedError = error as SerializedError;
      showToast({
        type: 'error',
        message: typedError.message || 'An error occurred while updating a glyph.',
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div>
      {isSending && (
        <div className="c-edit-element-modal-loader">
          <LoadingIndicator width={LOADING_INDICATOR_SIZE} height={LOADING_INDICATOR_SIZE} />
        </div>
      )}
      <div className="grid grid-cols-3 gap-x-3 gap-y-4">
        <div className="flex flex-col gap-1">
          <Autocomplete<MapModel>
            options={models}
            initialValue={initialSelectedMapModel}
            clearable
            valueKey="id"
            labelKey="name"
            onChange={handleMapModelChange}
          />
        </div>
      </div>
    </div>
  );
};
